import React from 'react';

export interface PenWritingLoaderProps {
  /** Width in px; height is derived from the 120x84 viewBox. */
  size?: number;
}

/**
 * Asset-free "pen writing on paper" loader for the content-creation overlay.
 * Pure SVG + CSS keyframes — no binary asset / Lottie dependency — using the
 * coral/teal theme tokens. The pen glides left-to-right while the ink trail is
 * revealed behind its nib (both share the same 2.2s loop so the nib stays at the
 * leading edge of the ink).
 */
export const PenWritingLoader: React.FC<PenWritingLoaderProps> = ({ size = 120 }) => {
  return (
    <div style={{ width: size, height: (size * 84) / 120 }} role="img" aria-label="Writing your article">
      <style>{`
        @keyframes pw-glide {
          0%   { transform: translateX(0); }
          85%  { transform: translateX(72px); }
          100% { transform: translateX(72px); }
        }
        @keyframes pw-ink {
          0%   { stroke-dashoffset: 72; }
          85%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 0; }
        }
        @keyframes pw-bob {
          0%, 100% { transform: translateY(0); }
          25%      { transform: translateY(-1.5px); }
          75%      { transform: translateY(1.5px); }
        }
        .pw-pen { animation: pw-glide 2.2s ease-in-out infinite; }
        .pw-nib { animation: pw-bob 0.4s ease-in-out infinite; }
        .pw-ink {
          stroke-dasharray: 72;
          animation: pw-ink 2.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .pw-pen, .pw-nib, .pw-ink { animation: none; }
          .pw-ink { stroke-dashoffset: 0; }
        }
      `}</style>
      <svg viewBox="0 0 120 84" width="100%" height="100%" fill="none">
        {/* paper */}
        <rect
          x="8" y="8" width="104" height="68" rx="6"
          fill="var(--color-paper)" stroke="var(--color-warm-border)" strokeWidth="2"
        />
        {/* faint guide lines already "written" */}
        <line x1="20" y1="30" x2="96" y2="30" stroke="var(--color-warm-border)" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="20" y1="45" x2="84" y2="45" stroke="var(--color-warm-border)" strokeWidth="2.5" strokeLinecap="round" />
        {/* the ink line being written this loop */}
        <path className="pw-ink" d="M20 60 H92" stroke="var(--color-teal-dark)" strokeWidth="3" strokeLinecap="round" />
        {/* the pen, gliding with its nib at the ink's leading edge (20,60) */}
        <g className="pw-pen">
          <g className="pw-nib">
            {/*
              One coherent pencil along a single axis (unit vector 0.6,-0.8 from
              the nib tip at 20,60). The previous version rotated only the barrel
              (rotate 42° about its own center), which left the cap dot floating
              ~7px off the barrel end and the barrel unseated from the nib.
            */}
            {/* barrel: nib base -> +20 along the axis */}
            <path
              d="M23.2 49.9 L28.8 54.1 L40.8 38.1 L35.2 33.9 Z"
              fill="var(--color-coral)"
            />
            {/* nib: tip at the ink's leading edge, base flush with the barrel */}
            <path d="M20 60 L23.2 49.9 L28.8 54.1 Z" fill="var(--color-coral-dark)" />
            {/* cap end: centered on the barrel's far end (rounded cap) */}
            <circle cx="38" cy="36" r="2.8" fill="var(--color-coral-dark)" />
          </g>
        </g>
      </svg>
    </div>
  );
};

export default PenWritingLoader;
